/**
 * attachmentsApi.ts
 *
 * Typed API wrappers over `graphClient` for sphere attachment CRUD.
 * Every function takes an `ApiClient` as its first argument so the caller
 * (hook, thunk, or component) owns auth/impersonation via the shared
 * `ApiClientsContext`.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SphereAttachment } from 'types/attachment';

/**
 * Paged response envelope returned by `GET /spheres/:sphereId/attachments`.
 */
export interface ListAttachmentsResponse {
  status: string;
  attachments: SphereAttachment[];
  nextCursor: string | null;
}

/** Query parameters for list paging. */
export interface ListAttachmentsParams {
  limit?: number;
  cursor?: string;
}

/** JSON body accepted by `POST /spheres/:sphereId/attachments` for link creation. */
export interface CreateLinkAttachmentPayload {
  url: string;
  name?: string;
  description?: string | null;
}

/** JSON body accepted by `PATCH /spheres/:sphereId/attachments/:id` for link edit. */
export interface UpdateLinkAttachmentPayload {
  url?: string;
  name?: string;
  description?: string | null;
}

/** Response envelope from create/get single attachment endpoints. */
export interface AttachmentEnvelope {
  status: string;
  attachment: SphereAttachment;
}

/** Response envelope from the content endpoint for files <=25 MB. */
export interface AttachmentSignedUrlResponse {
  signedUrl: string;
  expiresAt: string;
}

/**
 * List collection attachments for a sphere, ordered by most-recently uploaded.
 */
export async function listSphereAttachments(
  graphClient: ApiClient,
  sphereId: string,
  params: ListAttachmentsParams = {}
): Promise<ListAttachmentsResponse> {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.cursor) query.set('cursor', params.cursor);
  const suffix = query.toString();
  const querySegment = suffix ? `?${suffix}` : '';
  const url = `/spheres/${encodeURIComponent(sphereId)}/attachments${querySegment}`;
  return graphClient.get<ListAttachmentsResponse>(url);
}

/**
 * Fetch metadata for a single collection attachment.
 */
export async function getSphereAttachment(
  graphClient: ApiClient,
  sphereId: string,
  attachmentId: string
): Promise<SphereAttachment> {
  const res = await graphClient.get<AttachmentEnvelope>(
    `/spheres/${encodeURIComponent(sphereId)}/attachments/${encodeURIComponent(attachmentId)}`
  );
  return res.attachment;
}

/**
 * Upload a binary file attachment via multipart/form-data.
 * The browser sets the `Content-Type` (with boundary) automatically when a
 * `FormData` body is passed to axios.
 */
export async function uploadFileAttachment(
  graphClient: ApiClient,
  sphereId: string,
  file: File,
  description?: string
): Promise<SphereAttachment> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (description && description.trim().length > 0) {
    formData.append('description', description);
  }

  const res = await graphClient.post<AttachmentEnvelope>(
    `/spheres/${encodeURIComponent(sphereId)}/attachments`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      retries: 0,
    }
  );
  return res.attachment;
}

/**
 * Create an external-link attachment via JSON body.
 */
export async function createLinkAttachment(
  graphClient: ApiClient,
  sphereId: string,
  payload: CreateLinkAttachmentPayload
): Promise<SphereAttachment> {
  const res = await graphClient.post<AttachmentEnvelope>(
    `/spheres/${encodeURIComponent(sphereId)}/attachments`,
    payload
  );
  return res.attachment;
}

/**
 * Patch metadata on an existing link attachment. Server validates the body
 * (at least one field; URL must be http/https; file rows rejected with
 * `400 ATTACHMENT_NOT_LINK`). The returned row preserves `id`,
 * `uploaded_at`, and `uploaded_by`.
 */
export async function updateLinkAttachment(
  graphClient: ApiClient,
  sphereId: string,
  attachmentId: string,
  payload: UpdateLinkAttachmentPayload
): Promise<SphereAttachment> {
  const res = await graphClient.patch<AttachmentEnvelope>(
    `/spheres/${encodeURIComponent(sphereId)}/attachments/${encodeURIComponent(attachmentId)}`,
    payload
  );
  return res.attachment;
}

/**
 * Delete a collection attachment. Returns 404 on re-delete (idempotent at
 * the caller's discretion — this wrapper propagates the thrown error).
 */
export async function deleteSphereAttachment(
  graphClient: ApiClient,
  sphereId: string,
  attachmentId: string
): Promise<void> {
  await graphClient.delete(
    `/spheres/${encodeURIComponent(sphereId)}/attachments/${encodeURIComponent(attachmentId)}`
  );
}

/**
 * Request a signed URL for a small file attachment.
 * Links return a 302 redirect and large files are proxy-streamed — both
 * cases are unsuitable for this wrapper, so callers that may hit those
 * branches should navigate directly to the `/content` URL instead of
 * calling this function.
 *
 * `disposition` controls the `Content-Disposition` baked into the SAS:
 *   - `'inline'` (default) — required for PDF preview to render in the
 *     browser's <embed> viewer; safe for image/video too.
 *   - `'attachment'` — forces a download when the URL is navigated to.
 */
export async function getAttachmentSignedUrl(
  graphClient: ApiClient,
  sphereId: string,
  attachmentId: string,
  disposition: 'inline' | 'attachment' = 'inline',
): Promise<AttachmentSignedUrlResponse> {
  const qs = disposition === 'attachment' ? '?disposition=attachment' : '';
  return graphClient.get<AttachmentSignedUrlResponse>(
    `/spheres/${encodeURIComponent(sphereId)}/attachments/${encodeURIComponent(attachmentId)}/content${qs}`
  );
}
